import React, { useEffect, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { api } from "../lib/api";
import { NOTE_TEXT, splitMessage, targetGroupState } from "../lib/targetGroup";
import LocalCombobox from "./forms/LocalCombobox";

/**
 * <TeacherProfileFields />
 *
 * Two structured fields side-by-side on desktop, stacked on mobile:
 *   1. Ciclu de învățământ (multi-select checkbox group)
 *      → `teaching_levels: string[]`
 *      → `teaching_level_other: string` (only when "Altul" is checked)
 *
 *   2. Disciplina predată / specializare (searchable single-select)
 *      → `specialization: string`
 *      → `specialization_other: string` (only when "Altă disciplină")
 *
 * `targetGroup` — what to say when "Universitar" is ticked (02.10.2026):
 *   "block"  → the checkout's warning, in the error colours (the page stops);
 *   "inform" → the same text as a note (a form that enrols nobody);
 *   omitted  → nothing (the admin's editor).
 * The component only shows; stopping is the page's job (`useTeacherVocab`).
 *
 * Fully controlled component. The vocabulary is fetched once from
 * `/api/teacher-profile/vocabulary` on mount and cached on the global
 * `window` object so subsequent forms on the same page reuse it.
 *
 * Backward compatibility: when `value.specialization` is empty but the
 * parent has a legacy free-text `specializare` value, the parent can
 * pre-fill the specialization to "Altă disciplină" + push the old value
 * into `specialization_other`. Helper exported below as `coerceLegacy()`.
 */

const FIELD_CLS =
  "w-full rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45] " +
  "outline-none focus:bg-white focus:border-[#965EA7] transition-colors";

function FieldLabel({ children, required }) {
  return (
    <span className="block text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">
      {children}{required && <span className="text-[#965EA7]"> *</span>}
    </span>
  );
}

let _vocabCache = null;
async function fetchVocab() {
  if (_vocabCache) return _vocabCache;
  try {
    const r = await api.get("/teacher-profile/vocabulary");
    _vocabCache = r.data;
    return _vocabCache;
  } catch {
    return { teaching_levels: [], specializations: [] };
  }
}

/**
 * Normalizes a partially-populated value object so the component renders
 * legacy data correctly. Call this when seeding the form from a record
 * fetched from the backend (e.g. when editing an existing lead/enrollment).
 */
export function coerceLegacyTeacherProfile(legacy, allowedSpecializations) {
  const out = {
    teaching_levels: Array.isArray(legacy?.teaching_levels) ? legacy.teaching_levels : [],
    teaching_level_other: legacy?.teaching_level_other || "",
    specialization: legacy?.specialization || "",
    specialization_other: legacy?.specialization_other || "",
  };
  // If we don't have a structured `specialization` but the legacy free-text
  // `specializare` is populated, bucket it as "Altă disciplină".
  if (!out.specialization && legacy?.specializare) {
    if (allowedSpecializations && allowedSpecializations.includes(legacy.specializare)) {
      out.specialization = legacy.specializare;
    } else {
      out.specialization = "Altă disciplină";
      out.specialization_other = legacy.specializare;
    }
  }
  return out;
}

/** The vocabulary, shared with the page — which needs the target group's rule. */
export function useTeacherVocab() {
  const [vocab, setVocab] = useState(_vocabCache || { teaching_levels: [], specializations: [] });
  useEffect(() => {
    let alive = true;
    fetchVocab().then((data) => { if (alive) setVocab(data); });
    return () => { alive = false; };
  }, []);
  return vocab;
}

export default function TeacherProfileFields({
  value,
  onChange,
  required = true,
  targetGroup,
}) {
  const v = value || {};
  const vocab = useTeacherVocab();
  const tg = targetGroup ? targetGroupState(v.teaching_levels, vocab.target_group) : "ok";

  const toggleLevel = (lvl) => {
    const cur = Array.isArray(v.teaching_levels) ? v.teaching_levels : [];
    const next = cur.includes(lvl) ? cur.filter((x) => x !== lvl) : [...cur, lvl];
    const patch = { teaching_levels: next };
    if (!next.includes("Altul")) patch.teaching_level_other = "";
    onChange(patch);
  };

  const onSpecChange = (val) => {
    const patch = { specialization: val };
    if (val !== "Altă disciplină") patch.specialization_other = "";
    onChange(patch);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="teacher-profile-fields">
      {/* Teaching levels — multi-select */}
      <div data-testid="teaching-levels-field">
        <FieldLabel required={required}>Ciclu de învățământ în care predai</FieldLabel>
        <div className="rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] p-3 space-y-1.5">
          {vocab.teaching_levels.map((lvl) => {
            const checked = Array.isArray(v.teaching_levels) && v.teaching_levels.includes(lvl);
            return (
              <label
                key={lvl}
                className={
                  "flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer text-sm transition-colors " +
                  (checked ? "bg-[#F3E8F7] text-[#965EA7] font-medium" : "hover:bg-white text-[#373A45]")
                }
                data-testid={`teaching-level-${lvl}`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#965EA7]"
                  checked={checked}
                  onChange={() => toggleLevel(lvl)}
                />
                <span>{lvl}</span>
              </label>
            );
          })}
        </div>
        {Array.isArray(v.teaching_levels) && v.teaching_levels.includes("Altul") && (
          <input
            type="text"
            className={FIELD_CLS + " mt-2"}
            placeholder="Specifică alt ciclu (ex. învățământ vocațional)"
            value={v.teaching_level_other || ""}
            onChange={(e) => onChange({ teaching_level_other: e.target.value })}
            required={required}
            data-testid="teaching-level-other-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        )}
        {tg === "ok" && (
          <div className="mt-1.5 text-[11px] text-[#6B7280]">
            Poți selecta unul sau mai multe cicluri.
          </div>
        )}
        {tg === "blocked" && <TargetGroupWarning message={vocab.target_group?.message} block={targetGroup === "block"} />}
        {tg === "note" && (
          <p className="mt-2 flex gap-1.5 text-xs text-[#6B7280]" data-testid="target-group-note">
            <Info size={14} className="mt-px shrink-0 text-[#965EA7]" /> {NOTE_TEXT}
          </p>
        )}
      </div>

      {/* Specialization — single-select via bounded local combobox */}
      <div data-testid="specialization-field">
        <FieldLabel required={required}>Disciplina predată / specializare</FieldLabel>
        <LocalCombobox
          value={v.specialization || ""}
          onChange={onSpecChange}
          options={vocab.specializations}
          placeholder="Selectează disciplina"
          required={required}
          testid="specialization-select"
        />
        {v.specialization === "Altă disciplină" && (
          <input
            type="text"
            className={FIELD_CLS + " mt-2"}
            placeholder="Specifică disciplina"
            value={v.specialization_other || ""}
            onChange={(e) => onChange({ specialization_other: e.target.value })}
            required={required}
            data-testid="specialization-other-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        )}
      </div>
    </div>
  );
}

function TargetGroupWarning({ message, block }) {
  const [head, rest] = splitMessage(message);
  return (
    <div
      role="alert"
      className={
        "mt-2 flex gap-2.5 rounded-lg border p-3 text-sm leading-relaxed " +
        (block ? "border-[#c0483f]/30 bg-[#c0483f]/[0.06] text-[#373A45]" : "border-[#d99a2b]/40 bg-[#d99a2b]/10 text-[#373A45]")
      }
      data-testid="target-group-warning"
    >
      <AlertTriangle size={17} className={"mt-0.5 shrink-0 " + (block ? "text-[#c0483f]" : "text-[#b07a1c]")} />
      <p><strong className="font-semibold">{head}</strong> {rest}</p>
    </div>
  );
}
